/**
 * This file was auto-generated by Fern from our API Definition.
 */

import * as core from "../../../../core/index.js";
import * as LangfuseAPI from "../../../index.js";
import {
  mergeHeaders,
  mergeOnlyDefinedHeaders,
} from "../../../../core/headers.js";
import * as errors from "../../../../errors/index.js";

export declare namespace Opentelemetry {
  export interface Options {
    environment: core.Supplier<string>;
    /** Specify a custom URL to connect the client to. */
    baseUrl?: core.Supplier<string>;
    username?: core.Supplier<string | undefined>;
    password?: core.Supplier<string | undefined>;
    /** Override the X-Langfuse-Sdk-Name header */
    xLangfuseSdkName?: core.Supplier<string | undefined>;
    /** Override the X-Langfuse-Sdk-Version header */
    xLangfuseSdkVersion?: core.Supplier<string | undefined>;
    /** Override the X-Langfuse-Public-Key header */
    xLangfusePublicKey?: core.Supplier<string | undefined>;
    /** Additional headers to include in requests. */
    headers?: Record<
      string,
      string | core.Supplier<string | null | undefined> | null | undefined
    >;
  }

  export interface RequestOptions {
    /** The maximum time to wait for a response in seconds. */
    timeoutInSeconds?: number;
    /** The number of times to retry the request. Defaults to 2. */
    maxRetries?: number;
    /** A hook to abort the request. */
    abortSignal?: AbortSignal;
    /** Override the X-Langfuse-Sdk-Name header */
    xLangfuseSdkName?: string | undefined;
    /** Override the X-Langfuse-Sdk-Version header */
    xLangfuseSdkVersion?: string | undefined;
    /** Override the X-Langfuse-Public-Key header */
    xLangfusePublicKey?: string | undefined;
    /** Additional query string parameters to include in the request. */
    queryParams?: Record<string, unknown>;
    /** Additional headers to include in the request. */
    headers?: Record<
      string,
      string | core.Supplier<string | null | undefined> | null | undefined
    >;
  }
}

export class Opentelemetry {
  protected readonly _options: Opentelemetry.Options;

  constructor(_options: Opentelemetry.Options) {
    this._options = _options;
  }

  /**
   * **OpenTelemetry Traces Ingestion Endpoint**
   *
   * This endpoint implements the OTLP/HTTP specification for trace ingestion, providing native OpenTelemetry integration for Langfuse Observability.
   *
   * **Supported Formats:**
   * - Binary Protobuf: `Content-Type: application/x-protobuf`
   * - JSON Protobuf: `Content-Type: application/json`
   * - Supports gzip compression via `Content-Encoding: gzip` header
   *
   * **Specification Compliance:**
   * - Conforms to [OTLP/HTTP Trace Export](https://opentelemetry.io/docs/specs/otlp/#otlphttp)
   * - Implements `ExportTraceServiceRequest` message format
   *
   * **Documentation:**
   * - Integration guide: https://langfuse.com/integrations/native/opentelemetry
   * - Data model: https://langfuse.com/docs/observability/data-model
   *
   * @param {LangfuseAPI.OtelTraceRequest} request
   * @param {Opentelemetry.RequestOptions} requestOptions - Request-specific configuration.
   *
   * @throws {@link LangfuseAPI.Error}
   * @throws {@link LangfuseAPI.UnauthorizedError}
   * @throws {@link LangfuseAPI.AccessDeniedError}
   * @throws {@link LangfuseAPI.MethodNotAllowedError}
   * @throws {@link LangfuseAPI.NotFoundError}
   *
   * @example
   *     await client.opentelemetry.exportTraces({
   *         resourceSpans: [{
   *                 resource: {
   *                     attributes: [{
   *                             key: "service.name",
   *                             value: {
   *                                 stringValue: "my-service"
   *                             }
   *                         }, {
   *                             key: "service.version",
   *                             value: {
   *                                 stringValue: "1.0.0"
   *                             }
   *                         }]
   *                 },
   *                 scopeSpans: [{
   *                         scope: {
   *                             name: "langfuse-sdk",
   *                             version: "2.60.3"
   *                         },
   *                         spans: [{
   *                                 traceId: "0123456789abcdef0123456789abcdef",
   *                                 spanId: "0123456789abcdef",
   *                                 name: "my-operation",
   *                                 kind: 1,
   *                                 startTimeUnixNano: "1747872000000000000",
   *                                 endTimeUnixNano: "1747872001000000000",
   *                                 attributes: [{
   *                                         key: "langfuse.observation.type",
   *                                         value: {
   *                                             stringValue: "generation"
   *                                         }
   *                                     }],
   *                                 status: {}
   *                             }]
   *                     }]
   *             }]
   *     })
   */
  public exportTraces(
    request: LangfuseAPI.OtelTraceRequest,
    requestOptions?: Opentelemetry.RequestOptions,
  ): core.HttpResponsePromise<LangfuseAPI.OtelTraceResponse> {
    return core.HttpResponsePromise.fromPromise(
      this.__exportTraces(request, requestOptions),
    );
  }

  private async __exportTraces(
    request: LangfuseAPI.OtelTraceRequest,
    requestOptions?: Opentelemetry.RequestOptions,
  ): Promise<core.WithRawResponse<LangfuseAPI.OtelTraceResponse>> {
    let _headers: core.Fetcher.Args["headers"] = mergeHeaders(
      this._options?.headers,
      mergeOnlyDefinedHeaders({
        Authorization: await this._getAuthorizationHeader(),
        "X-Langfuse-Sdk-Name":
          requestOptions?.xLangfuseSdkName ?? this._options?.xLangfuseSdkName,
        "X-Langfuse-Sdk-Version":
          requestOptions?.xLangfuseSdkVersion ??
          this._options?.xLangfuseSdkVersion,
        "X-Langfuse-Public-Key":
          requestOptions?.xLangfusePublicKey ??
          this._options?.xLangfusePublicKey,
      }),
      requestOptions?.headers,
    );
    const _response = await core.fetcher({
      url: core.url.join(
        (await core.Supplier.get(this._options.baseUrl)) ??
          (await core.Supplier.get(this._options.environment)),
        "/api/public/otel/v1/traces",
      ),
      method: "POST",
      headers: _headers,
      contentType: "application/json",
      queryParameters: requestOptions?.queryParams,
      requestType: "json",
      body: request,
      timeoutMs:
        requestOptions?.timeoutInSeconds != null
          ? requestOptions.timeoutInSeconds * 1000
          : 60000,
      maxRetries: requestOptions?.maxRetries,
      abortSignal: requestOptions?.abortSignal,
    });
    if (_response.ok) {
      return {
        data: _response.body as LangfuseAPI.OtelTraceResponse,
        rawResponse: _response.rawResponse,
      };
    }

    if (_response.error.reason === "status-code") {
      switch (_response.error.statusCode) {
        case 400:
          throw new LangfuseAPI.Error(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 401:
          throw new LangfuseAPI.UnauthorizedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 403:
          throw new LangfuseAPI.AccessDeniedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 405:
          throw new LangfuseAPI.MethodNotAllowedError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        case 404:
          throw new LangfuseAPI.NotFoundError(
            _response.error.body as unknown,
            _response.rawResponse,
          );
        default:
          throw new errors.LangfuseAPIError({
            statusCode: _response.error.statusCode,
            body: _response.error.body,
            rawResponse: _response.rawResponse,
          });
      }
    }

    switch (_response.error.reason) {
      case "non-json":
        throw new errors.LangfuseAPIError({
          statusCode: _response.error.statusCode,
          body: _response.error.rawBody,
          rawResponse: _response.rawResponse,
        });
      case "timeout":
        throw new errors.LangfuseAPITimeoutError(
          "Timeout exceeded when calling POST /api/public/otel/v1/traces.",
        );
      case "unknown":
        throw new errors.LangfuseAPIError({
          message: _response.error.errorMessage,
          rawResponse: _response.rawResponse,
        });
    }
  }

  protected async _getAuthorizationHeader(): Promise<string | undefined> {
    const username = await core.Supplier.get(this._options.username);
    const password = await core.Supplier.get(this._options.password);
    if (username != null && password != null) {
      return core.BasicAuth.toAuthorizationHeader({
        username: username,
        password: password,
      });
    }

    return undefined;
  }
}
